import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, User, Store, ShieldCheck, Sparkles } from "lucide-react";
import { API } from "../lib/api";
import { BRANDS, PRICE_BUCKETS, STATES } from "../data/india";
import StateCitySelect from "../components/StateCitySelect";
import CarCard from "../components/CarCard";
import DealerCard from "../components/DealerCard";
import BannerSlider from "../components/BannerSlider";
import HeroSlider from "../components/HeroSlider";
import { useUserLocation } from "../lib/location";
import NearbyCars from "../components/NearbyCars";

const HERO = "https://images.unsplash.com/photo-1769674027488-d28af3f6ccb8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000";
const PRIVATE_IMG = "https://images.unsplash.com/photo-1663852408695-f57f4d75a536?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";
const DEALER_IMG = "https://images.unsplash.com/photo-1783740486439-b4487fad649f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";
const WRAP = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

function HeroSearch() {
  const navigate = useNavigate();
  const [f, setF] = useState({ seller_type: "", state: "", city: "", brand: "", price: "" });
  const go = () => {
    const p = new URLSearchParams();
    ["seller_type", "state", "city", "brand"].forEach((k) => f[k] && p.set(k, f[k]));
    if (f.price !== "") {
      const b = PRICE_BUCKETS[f.price];
      if (b.min) p.set("min_price", b.min);
      if (b.max) p.set("max_price", b.max);
    }
    navigate(`/cars?${p.toString()}`);
  };
  const tabs = [["", "All Cars"], ["private", "Private Owners"], ["dealer", "Dealer Stores"]];
  return (
    <div className="bg-black/60 backdrop-blur-xl border border-[#D4AF37]/30 rounded-2xl p-4 shadow-2xl rise" style={{ animationDelay: "200ms" }} data-testid="hero-search">
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {tabs.map(([v, l]) => (
          <button key={v} onClick={() => setF({ ...f, seller_type: v })} data-testid={`seller-type-tab-${v || "all"}`}
            className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors ${f.seller_type === v ? "text-black" : "text-gray-300 border border-white/10 hover:border-[#D4AF37]/50"}`}
            style={f.seller_type === v ? { background: "var(--gold-grad)" } : {}}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 items-end">
        <StateCitySelect state={f.state} city={f.city} allowAll prefix="hero-" onChange={(v) => setF({ ...f, ...v })} />
        <div>
          <label className="lux-label">Brand</label>
          <select className="lux-select" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="hero-brand-select">
            <option value="">Any brand</option>{BRANDS.map((b) => <option key={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="lux-label">Budget</label>
          <select className="lux-select" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="hero-price-select">
            <option value="">Any budget</option>{PRICE_BUCKETS.map((b, i) => <option key={b.label} value={i}>{b.label}</option>)}
          </select>
        </div>
        <button onClick={go} className="btn-gold h-[36px] col-span-2 lg:col-span-1" data-testid="hero-search-button"><Search className="w-3.5 h-3.5" /> Search</button>
      </div>
    </div>
  );
}

function CategoryCard({ to, img, icon: Icon, eyebrow, title, text, count, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group relative overflow-hidden rounded-xl border gold-line min-h-[84px] flex items-center">
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
      <div className="relative px-3 py-2.5 flex items-center gap-3 w-full">
        <span className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center" style={{ background: "var(--gold-grad)" }}><Icon className="w-3.5 h-3.5 text-black" /></span>
        <div className="min-w-0 flex-1">
          <p className="eyebrow !text-[8px]">{eyebrow}</p>
          <h3 className="font-display text-lg text-white leading-tight">{title}</h3>
          <p className="text-[11px] text-gray-300 line-clamp-1">{text}</p>
        </div>
        <p className="text-[#E5C158] text-xs hidden sm:flex items-center gap-1 shrink-0">{count} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></p>
      </div>
    </Link>
  );
}

const SectionHead = ({ eyebrow, title, to, label, testid }) => (
  <div className="flex items-end justify-between mb-4 gap-4">
    <div><p className="eyebrow !text-[10px] mb-1">{eyebrow}</p><h2 className="font-display text-2xl sm:text-3xl">{title}</h2></div>
    {to && <Link to={to} className="btn-outline !py-1.5 !px-3 text-xs" data-testid={testid}>{label} <ArrowRight className="w-3.5 h-3.5" /></Link>}
  </div>
);

export default function Home() {
  const [latest, setLatest] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [stats, setStats] = useState(null);
  const { location } = useUserLocation();
  const city = location?.city;
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const loc = city ? { state: location.state, city } : {};
    setLoaded(false);
    axios.get(`${API}/listings`, { params: { limit: 8, ...loc } }).then((r) => { setLatest(r.data.items); setLoaded(true); });
    axios.get(`${API}/dealers`, { params: loc }).then((r) => setDealers(r.data.slice(0, 4)));
  }, [city]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSlider
        top={<BannerSlider className="mb-6 rise" />}
        aside={stats && (
          <div className="flex gap-5 rise" style={{ animationDelay: "160ms" }} data-testid="home-stats">
            {[["Cars", stats.listings], ["Dealers", stats.dealers], ["Owners", stats.private], ["States", STATES.length]].map(([l, v]) => (
              <div key={l} className="border-l gold-line pl-2.5"><p className="font-display text-2xl gold-text leading-none" data-testid={`home-stat-${l.toLowerCase()}`}>{v}</p><p className="text-[9px] text-gray-400 uppercase tracking-widest mt-1">{l}</p></div>
            ))}
          </div>
        )}
      >
        <HeroSearch />
      </HeroSlider>

      <section className={`${WRAP} py-6`}>
        <div className="grid md:grid-cols-2 gap-3">
          <CategoryCard to="/cars?seller_type=private" img={PRIVATE_IMG} icon={User} eyebrow="Category 01" title="Private Owners" text="Buy directly from individual owners. No middlemen." count={`${stats?.private ?? "—"} cars`} testid="category-private" />
          <CategoryCard to="/dealers" img={DEALER_IMG} icon={Store} eyebrow="Category 02" title="Pre-Owned Dealers" text="Showroom inventories with store profiles & verified badges." count={`${stats?.dealers ?? "—"} stores`} testid="category-dealer" />
        </div>
      </section>

      <section className={`${WRAP} py-6`}>
        <SectionHead eyebrow={city ? `Only in ${city}` : "Featured & fresh"} title={city ? `Latest cars in ${city}` : "Latest listings"} to="/cars" label="View all" testid="view-all-cars" />
        {latest.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="home-latest-grid">{latest.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}</div>
        ) : (
          <div className="lux-card p-8 text-center text-gray-400 text-sm" data-testid="home-latest-empty">
            {city ? <>No cars listed in {city} yet. <Link to="/sell" className="text-[#E5C158]">List yours free</Link> or <Link to="/cars?all=1" className="text-[#E5C158]" data-testid="home-see-all-india">see all India</Link>.</> : "No cars listed yet."}
          </div>
        )}
      </section>

      {city && loaded && <NearbyCars state={location.state} city={city} cityCount={latest.length} className={`${WRAP} py-6`} />}

      {dealers.length > 0 && (
        <section className={`${WRAP} py-6`}>
          <SectionHead eyebrow="Showrooms" title={city ? `Dealer stores in ${city}` : "Featured dealer stores"} to="/dealers" label="All dealers" testid="view-all-dealers" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{dealers.map((d) => <DealerCard key={d.id} dealer={d} />)}</div>
        </section>
      )}

      <section className={`${WRAP} py-6`}>
        <SectionHead eyebrow="Pan-India" title="Browse by state" to="/cities" label="All cities" testid="view-all-cities" />
        <div className="flex flex-wrap gap-1.5" data-testid="state-chips">
          {STATES.map((s) => (
            <Link key={s} to={`/cars?state=${encodeURIComponent(s)}`} className="px-3 py-1 rounded-full border border-white/10 text-xs text-gray-300 hover:border-[#D4AF37] hover:text-[#F5E08F] transition-colors">{s}</Link>
          ))}
        </div>
      </section>

      <section className={`${WRAP} pt-6`}>
        <div className="relative overflow-hidden rounded-2xl border gold-line p-5 sm:p-6 grain bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.18),transparent_60%)] bg-[#0d0f14]">
          <div className="relative z-10 flex flex-wrap gap-4 items-center justify-between">
            <div>
              <p className="eyebrow !text-[10px] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Free to list</p>
              <h2 className="font-display text-2xl sm:text-3xl">Sell your car to serious buyers.</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/sell" className="btn-gold text-sm" data-testid="cta-sell-private">Sell as Owner</Link>
              <Link to="/dashboard?tab=store" className="btn-outline text-sm" data-testid="cta-open-store">Open Dealer Store</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
